import Phaser from 'phaser';

// Style Audition. Visit #audition in the URL.
// Renders a demo room with 4 toggleable visual treatments so Ian can compare
// at full scale and pick a direction before we commit to art production.

const STYLE_KEY = 'ian_world_style';
const TILE = 32; // 16px logical * 2x scale, matches WORLD_CONFIG
const ROOM_W_TILES = 20;
const ROOM_H_TILES = 13;

const TREATMENTS = [
  {
    id: 'A',
    name: 'Plain placeholder',
    blurb: 'What you have today. Top-down, flat colors, no lighting. Honest baseline.',
    cost: 'Already shipped. No new work.',
  },
  {
    id: 'B',
    name: 'Cozy lit',
    blurb: 'Dark overlay with warm light cutouts at the drone, lamp, and interactive objects. Edge vignette. Feels lived-in immediately.',
    cost: 'Programmatic. ~1 day. No new assets needed.',
  },
  {
    id: 'C',
    name: 'Painterly warm',
    blurb: 'Sepia/honey color grade across the whole room + soft dust motes drifting + subtle bloom on bright spots. Hand-drawn feel without redraws.',
    cost: 'Programmatic. ~1 day. No new assets needed.',
  },
  {
    id: 'D',
    name: 'Atmospheric depth',
    blurb: 'Mimics the Claude Code OS reference. Heavy darkness, dramatic warm lights, strong vignette, window-glow beam. Best paired with AI-generated assets — open Studio next.',
    cost: 'Programmatic shell ready. Real impact needs ~10-15 generated assets.',
  },
];

export class StyleAuditionScene extends Phaser.Scene {
  constructor() {
    super('StyleAuditionScene');
  }

  create() {
    const cam = this.cameras.main;
    cam.setBackgroundColor('#0a0806');
    cam.fadeIn(280, 0, 0, 0);

    this.activeTreatment = 'A';
    this.savedSelection = (() => {
      try { return window.localStorage.getItem(STYLE_KEY); } catch (_) { return null; }
    })();

    this._buildHeader();
    this._buildTabs();
    this._buildRoomContainer();
    this._buildOverlayContainer();
    this._buildFooter();
    this._buildSelectButton();
    this._renderTreatment('A');
    this._bindKeys();
  }

  // Header ───────────────────────────────────────────────────────────────
  _buildHeader() {
    const cam = this.cameras.main;
    this.add.rectangle(0, 0, cam.width, 60, 0x14110e, 1).setOrigin(0, 0).setDepth(900);
    this.add.text(28, 14, 'STYLE AUDITION', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#f6b93b',
      fontStyle: 'bold',
    }).setLetterSpacing?.(3).setDepth(901);
    this.add.text(28, 36, 'Same room, four treatments. Click a tab — pick the direction that resonates.', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#f4ead5',
    }).setAlpha(0.65).setDepth(901);

    if (this.savedSelection) {
      this.savedHint = this.add.text(cam.width - 28, 30, `current pick: ${this.savedSelection}`, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#9bd28a',
      }).setOrigin(1, 0.5).setAlpha(0.85).setDepth(901);
    }
  }

  // Tabs ─────────────────────────────────────────────────────────────────
  _buildTabs() {
    const tabsY = 76;
    const tabW = 130;
    const tabH = 32;
    const tabGap = 6;
    const totalW = TREATMENTS.length * tabW + (TREATMENTS.length - 1) * tabGap;
    const startX = (this.cameras.main.width - totalW) / 2;

    this.tabs = {};
    TREATMENTS.forEach((t, i) => {
      const tx = startX + i * (tabW + tabGap);
      const bg = this.add.rectangle(tx, tabsY, tabW, tabH, 0x14110e, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0xf4ead5, 0.25)
        .setInteractive({ useHandCursor: true });
      const labelText = this.add.text(tx + tabW / 2, tabsY + tabH / 2, `${t.id} · ${t.name}`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#f4ead5',
      }).setOrigin(0.5);
      bg.on('pointerdown', () => this._renderTreatment(t.id));
      this.tabs[t.id] = { bg, labelText };
    });
  }

  _highlightActiveTab() {
    for (const [id, tab] of Object.entries(this.tabs)) {
      const isActive = id === this.activeTreatment;
      tab.bg.setFillStyle(isActive ? 0x2c1f12 : 0x14110e, 1);
      tab.bg.setStrokeStyle(1, 0xf6b93b, isActive ? 0.85 : 0);
      tab.bg.setStrokeStyle(1, isActive ? 0xf6b93b : 0xf4ead5, isActive ? 0.85 : 0.25);
      tab.labelText.setColor(isActive ? '#f6b93b' : '#f4ead5');
    }
  }

  // Room canvas ──────────────────────────────────────────────────────────
  _buildRoomContainer() {
    const cam = this.cameras.main;
    this.roomW = ROOM_W_TILES * TILE;
    this.roomH = ROOM_H_TILES * TILE;
    this.roomX = (cam.width - this.roomW) / 2;
    this.roomY = 130;

    // Frame around the room
    this.add.rectangle(this.roomX - 4, this.roomY - 4, this.roomW + 8, this.roomH + 8, 0x000000, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0xf4ead5, 0.18);

    this.roomContainer = this.add.container(this.roomX, this.roomY);
    this._drawRoomBase();
  }

  _drawRoomBase() {
    const tile = TILE;
    const w = ROOM_W_TILES;
    const h = ROOM_H_TILES;

    const floorColor = 0x6b4f3a;
    const wallColor = 0x3a2a1f;
    const floor = Phaser.Display.Color.IntegerToColor(floorColor);
    const floorDark = floor.clone().darken(10).color;
    const grain = floor.clone().darken(20).color;
    const wall = Phaser.Display.Color.IntegerToColor(wallColor);
    const wallTop = wall.clone().lighten(20).color;
    const wallShadow = wall.clone().darken(15).color;

    const g = this.add.graphics();

    // Floor
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const offset = y % 2 === 0 ? 0 : 1;
        const plankIdx = Math.floor((x + offset) / 3);
        const base = plankIdx % 2 === 0 ? floorColor : floorDark;
        g.fillStyle(base, 1);
        g.fillRect(x * tile, y * tile, tile, tile);
        g.fillStyle(grain, 0.55);
        g.fillRect(x * tile, y * tile, tile, 1);
      }
    }

    // Walls
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        const isWall = x === 0 || y === 0 || x === w - 1 || y === h - 1;
        if (!isWall) continue;
        g.fillStyle(wallColor, 1);
        g.fillRect(x * tile, y * tile, tile, tile);
        if (y === 0) {
          g.fillStyle(wallTop, 1);
          g.fillRect(x * tile, y * tile, tile, 2);
          g.fillStyle(wallShadow, 1);
          g.fillRect(x * tile, y * tile + tile - 3, tile, 3);
        }
      }
    }

    // Top-wall inner shadow band
    g.fillStyle(0x000000, 0.18);
    g.fillRect(tile, tile, (w - 2) * tile, 3);

    this.roomContainer.add(g);

    // Demo objects: Currently board, NPC Ian, Drone
    this._addRoomSprite('object-currently-board', 6, 3);
    this._addRoomSprite('npc-pixel-ian', 12, 8);
    this.droneSprite = this._addRoomSprite('drone-default', 9, 9);

    // Track key positions for lighting cutouts
    this.lightSources = [
      { x: 6 * tile + tile / 2, y: 3 * tile + tile / 2, radius: 80, label: 'board' },
      { x: 12 * tile + tile / 2, y: 8 * tile + tile / 2, radius: 60, label: 'ian' },
      { x: 9 * tile + tile / 2, y: 9 * tile + tile / 2, radius: 100, label: 'drone' },
      // Window/lamp at top-center
      { x: (w / 2) * tile, y: tile, radius: 110, label: 'window' },
    ];
  }

  _addRoomSprite(textureKey, tx, ty) {
    if (!this.textures.exists(textureKey)) return null;
    const sprite = this.add.image(tx * TILE + TILE / 2, ty * TILE + TILE / 2, textureKey)
      .setScale(2);
    this.roomContainer.add(sprite);
    return sprite;
  }

  // Overlay container — recreated per treatment ──────────────────────────
  _buildOverlayContainer() {
    this.overlayContainer = this.add.container(this.roomX, this.roomY).setDepth(50);
  }

  _renderTreatment(id) {
    this.activeTreatment = id;
    this._highlightActiveTab();
    this.overlayContainer.removeAll(true);
    this._stopParticles();

    if (id === 'A') this._treatmentA();
    else if (id === 'B') this._treatmentB();
    else if (id === 'C') this._treatmentC();
    else if (id === 'D') this._treatmentD();

    this._updateBlurb(id);
  }

  // A: nothing
  _treatmentA() {
    // Pure baseline. No overlay.
  }

  // B: real cutout darkness — RenderTexture filled dark, erased at light positions
  // so the scene shows through the lit zones. Plus warm-glow tint over the cutouts.
  _treatmentB() {
    this._buildDarknessRT({
      darkColor: 0x05030a,
      darkAlpha: 0.62,
      lights: this.lightSources,
      eraseScale: 1.0,
    });
    this._addWarmGlow(this.lightSources, 0.7, 0.32, 0xffc880);
    this._drawVignette(0.4, 0x000000);
  }

  // C: warm color grade + dust motes + subtle bloom — no darkening, keeps scene readable
  _treatmentC() {
    // Gentle warm wash via NORMAL blend at low alpha — adds warmth without darkening
    const wash = this.add.graphics();
    wash.fillStyle(0xffd9a0, 0.13);
    wash.fillRect(0, 0, this.roomW, this.roomH);
    this.overlayContainer.add(wash);

    // Soft warm top-window light cascade
    const topGlow = this.add.graphics();
    topGlow.setBlendMode(Phaser.BlendModes.ADD);
    for (let i = 0; i < 10; i++) {
      const alpha = 0.07 - i * 0.006;
      topGlow.fillStyle(0xffd9a0, Math.max(0, alpha));
      topGlow.fillRect(0, 0, this.roomW, TILE * 2 + i * 6);
    }
    this.overlayContainer.add(topGlow);

    // Bloom on bright spots
    const bloom = this.add.graphics();
    bloom.setBlendMode(Phaser.BlendModes.ADD);
    for (const ls of this.lightSources) {
      this._drawRadialLight(bloom, ls.x, ls.y, ls.radius * 0.5, 0xffd9a0, 0.35);
    }
    this.overlayContainer.add(bloom);

    this._startDustMotes();
    this._drawVignette(0.18, 0x4a2818);
  }

  // D: heavy darkness with real cutouts — Claude Code OS feel. Bigger lights, stronger
  // warm tint inside the cutouts, dramatic vignette.
  _treatmentD() {
    this._buildDarknessRT({
      darkColor: 0x06040c,
      darkAlpha: 0.82,
      lights: this.lightSources,
      eraseScale: 1.15,
    });
    this._addWarmGlow(this.lightSources, 0.6, 0.45, 0xffb860);

    // Window beam — additive trapezoid from top-center
    const beam = this.add.graphics();
    beam.setBlendMode(Phaser.BlendModes.ADD);
    const beamCenter = this.roomW / 2;
    for (let i = 0; i < 12; i++) {
      const alpha = 0.06 - i * 0.0045;
      const halfW = 55 + i * 7;
      beam.fillStyle(0xffd9a0, Math.max(0, alpha));
      beam.fillTriangle(
        beamCenter - halfW, TILE,
        beamCenter + halfW, TILE,
        beamCenter, TILE + 80 + i * 8,
      );
    }
    this.overlayContainer.add(beam);

    this._drawVignette(0.6, 0x000000);
  }

  // Real darkness with cutouts via RenderTexture + erase
  _buildDarknessRT({ darkColor, darkAlpha, lights, eraseScale }) {
    this._ensureLightGradientTexture();
    const rt = this.add.renderTexture(0, 0, this.roomW, this.roomH);
    rt.fill(darkColor, darkAlpha);

    for (const ls of lights) {
      const eraser = this.make.image({ key: 'iw-light-gradient', add: false });
      const size = ls.radius * 2 * eraseScale;
      eraser.setDisplaySize(size, size);
      rt.erase(eraser, ls.x, ls.y);
      eraser.destroy();
    }
    this.overlayContainer.add(rt);
    return rt;
  }

  // Ensure the radial-gradient eraser texture exists (white center → transparent edge)
  _ensureLightGradientTexture() {
    const key = 'iw-light-gradient';
    if (this.textures.exists(key)) return;
    const size = 256;
    const cx = size / 2;
    const g = this.make.graphics({ x: 0, y: 0, add: false });
    // Build the gradient by drawing concentric rings of decreasing alpha
    for (let r = cx; r > 0; r -= 1) {
      const t = 1 - r / cx;
      const a = Math.pow(t, 1.6);
      g.fillStyle(0xffffff, a);
      g.fillCircle(cx, cx, r);
    }
    g.generateTexture(key, size, size);
    g.destroy();
  }

  // Additive warm glow on top of the cutouts to tint the lit zones warmer
  _addWarmGlow(lights, radiusScale, peakAlpha, color) {
    const layer = this.add.graphics();
    layer.setBlendMode(Phaser.BlendModes.ADD);
    for (const ls of lights) {
      this._drawRadialLight(layer, ls.x, ls.y, ls.radius * radiusScale, color, peakAlpha);
    }
    this.overlayContainer.add(layer);
  }

  _drawRadialLight(graphics, cx, cy, maxR, color, peakAlpha) {
    // Approximate a radial gradient with stacked circles — center bright, fading out
    const rings = 12;
    for (let i = 0; i < rings; i++) {
      const r = maxR * (1 - i / rings);
      const a = peakAlpha * Math.pow(i / rings, 2.2);
      graphics.fillStyle(color, a);
      graphics.fillCircle(cx, cy, r);
    }
  }

  _drawVignette(intensity, color) {
    const v = this.add.graphics();
    const layers = 14;
    for (let i = 0; i < layers; i++) {
      const inset = i * 6;
      if (inset * 2 >= this.roomW || inset * 2 >= this.roomH) break;
      const alpha = (intensity * (1 - i / layers)) / layers;
      v.lineStyle(7, color, alpha);
      v.strokeRect(inset, inset, this.roomW - inset * 2, this.roomH - inset * 2);
    }
    this.overlayContainer.add(v);
  }

  _startDustMotes() {
    this._stopParticles();
    this.dustMotes = [];
    for (let i = 0; i < 18; i++) {
      const x = Math.random() * this.roomW;
      const y = Math.random() * this.roomH;
      const dot = this.add.circle(x, y, 1, 0xffe6b8, 0.55);
      dot.setBlendMode(Phaser.BlendModes.ADD);
      this.overlayContainer.add(dot);
      const tween = this.tweens.add({
        targets: dot,
        y: y + 30 + Math.random() * 20,
        x: x + (Math.random() - 0.5) * 20,
        alpha: { from: 0.55, to: 0.1 },
        duration: 4000 + Math.random() * 3000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.dustMotes.push({ dot, tween });
    }
  }

  _stopParticles() {
    if (!this.dustMotes) return;
    for (const m of this.dustMotes) {
      m.tween.stop();
      m.dot.destroy();
    }
    this.dustMotes = [];
  }

  // Footer + select button ───────────────────────────────────────────────
  _buildFooter() {
    const cam = this.cameras.main;
    const fy = cam.height - 110;
    this.add.rectangle(0, fy, cam.width, 110, 0x14110e, 1).setOrigin(0, 0).setDepth(900);
    this.blurbTitle = this.add.text(28, fy + 14, '', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#f6b93b',
      fontStyle: 'bold',
    }).setDepth(901);
    this.blurbBody = this.add.text(28, fy + 34, '', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#f4ead5',
      wordWrap: { width: cam.width - 240 },
    }).setAlpha(0.85).setDepth(901);
    this.blurbCost = this.add.text(28, fy + 80, '', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#9bd28a',
    }).setAlpha(0.85).setDepth(901);
  }

  _updateBlurb(id) {
    const t = TREATMENTS.find((x) => x.id === id);
    if (!t) return;
    this.blurbTitle.setText(`${t.id} · ${t.name}`);
    this.blurbBody.setText(t.blurb);
    this.blurbCost.setText(`Cost: ${t.cost}`);
  }

  _buildSelectButton() {
    const cam = this.cameras.main;
    const fy = cam.height - 110;
    const btnW = 180;
    const btnH = 36;
    const bx = cam.width - btnW - 28;
    const by = fy + 36;
    const bg = this.add.rectangle(bx, by, btnW, btnH, 0xf6b93b, 1)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(901);
    const label = this.add.text(bx + btnW / 2, by + btnH / 2, 'select this direction', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#14110e',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(902);
    bg.on('pointerdown', () => this._selectCurrent());

    const hint = this.add.text(cam.width - 28, fy + 80, 'esc · exit audition    L · open Asset Lab', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#f4ead5',
    }).setOrigin(1, 0).setAlpha(0.55).setDepth(901);
  }

  _selectCurrent() {
    try { window.localStorage.setItem(STYLE_KEY, this.activeTreatment); } catch (_) {}
    this.savedSelection = this.activeTreatment;
    if (this.savedHint) {
      this.savedHint.setText(`current pick: ${this.activeTreatment}`);
    }
    // Brief confirmation flash
    const flash = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0xf6b93b, 0.18)
      .setOrigin(0, 0)
      .setDepth(950);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 300,
      onComplete: () => flash.destroy(),
    });
  }

  // Keys ─────────────────────────────────────────────────────────────────
  _bindKeys() {
    this.input.keyboard.on('keydown', (ev) => {
      if (ev.key === 'Escape') this._exit('HouseScene');
      else if (ev.key === 'l' || ev.key === 'L') this._exit('LabScene');
      else if (ev.key === '1') this._renderTreatment('A');
      else if (ev.key === '2') this._renderTreatment('B');
      else if (ev.key === '3') this._renderTreatment('C');
      else if (ev.key === '4') this._renderTreatment('D');
    });
  }

  _exit(target) {
    if (this._exiting) return;
    this._exiting = true;
    if (window.location.hash === '#audition') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start(target));
  }
}

export function getSavedStyle() {
  try { return window.localStorage.getItem(STYLE_KEY); } catch (_) { return null; }
}
